import type { MediaMessageType, Message } from '@/interfaces/message'
import type {
  AttachmentPreview,
  BuildOptimisticMessagesParams,
  BuildOptimisticRecordedAudioMessageParams,
} from './types'

export function createAttachmentPreview(file: File): AttachmentPreview {
  const type = resolveMediaType(file.type)
  const previewUrl = createPreviewUrl(file, type)

  return {
    id: crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2),
    file,
    previewUrl,
    type,
    mimeType: file.type,
    size: file.size,
  }
}

export function resolveMediaType(mime: string): MediaMessageType {
  if (mime.startsWith('image/')) {
    return 'image'
  }
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) {
    return mime.includes('ogg') ? 'ptt' : 'audio'
  }
  return 'document'
}

export function createPreviewUrl(file: File, type: MediaMessageType) {
  if (['image', 'video'].includes(type) || type === 'audio' || type === 'ptt') {
    return URL.createObjectURL(file)
  }
  return undefined
}

export function revokeAttachmentPreview(attachment: AttachmentPreview) {
  if (attachment.previewUrl) {
    URL.revokeObjectURL(attachment.previewUrl)
  }
}

export function buildOptimisticMessages({
  attachments,
  message,
  ticketId,
  contactId,
  whatsappId,
}: BuildOptimisticMessagesParams): Message[] {
  const timestamp = new Date().toISOString()
  const messages: Message[] = []

  if (!attachments.length && message) {
    return [
      {
        id: `temp-${timestamp}`,
        read: false,
        mediaUrl: '',
        mediaKey: '',
        mediaType: 'text',
        filename: '',
        type: 'text',
        from: '',
        fromMe: true,
        to: '',
        mimetype: '',
        timestamp,
        caption: '',
        mediaDuration: 0,
        mediaHeight: 0,
        mediaWidth: 0,
        mediaThumbnailUrl: '',
        quotedMsgId: '',
        authorId: '',
        contactId,
        whatsappId,
        ticketId,
        content: message,
        createdAt: timestamp,
        updatedAt: timestamp,
      },
    ]
  }

  attachments.forEach((attachment, index) => {
    messages.push({
      id: `temp-${timestamp}-${index}`,
      read: false,
      mediaUrl: attachment.previewUrl ?? '',
      mediaKey: '',
      mediaType: attachment.type,
      filename: attachment.file.name,
      type: attachment.type,
      from: '',
      fromMe: true,
      to: '',
      mimetype: attachment.mimeType,
      timestamp,
      caption: '',
      mediaDuration: 0,
      mediaHeight: 0,
      mediaWidth: 0,
      mediaThumbnailUrl: attachment.previewUrl ?? '',
      quotedMsgId: '',
      authorId: '',
      contactId,
      whatsappId,
      ticketId,
      content: attachment.file.name,
      createdAt: timestamp,
      updatedAt: timestamp,
    })
  })

  if (message && attachments.length > 0) {
    messages.push({
      id: `temp-${timestamp}-text`,
      read: false,
      mediaUrl: '',
      mediaKey: '',
      mediaType: 'text',
      filename: '',
      type: 'text',
      from: '',
      fromMe: true,
      to: '',
      mimetype: '',
      timestamp,
      caption: '',
      mediaDuration: 0,
      mediaHeight: 0,
      mediaWidth: 0,
      mediaThumbnailUrl: '',
      quotedMsgId: '',
      authorId: '',
      contactId,
      whatsappId,
      ticketId,
      content: message,
      createdAt: timestamp,
      updatedAt: timestamp,
    })
  }

  return messages
}

export function buildOptimisticRecordedAudioMessage({
  tempId,
  file,
  mimeType,
  previewUrl,
  durationSeconds,
  ticketId,
  contactId,
  whatsappId,
}: BuildOptimisticRecordedAudioMessageParams): Message {
  const timestamp = new Date().toISOString()

  return {
    id: tempId,
    read: false,
    mediaUrl: previewUrl,
    mediaKey: '',
    mediaType: 'ptt',
    filename: file.name,
    type: 'ptt',
    from: '',
    fromMe: true,
    to: '',
    mimetype: mimeType,
    timestamp,
    caption: '',
    mediaDuration: durationSeconds,
    mediaHeight: 0,
    mediaWidth: 0,
    mediaThumbnailUrl: previewUrl,
    quotedMsgId: '',
    authorId: '',
    contactId,
    whatsappId,
    ticketId,
    content: file.name,
    createdAt: timestamp,
    updatedAt: timestamp,
  }
}

export function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes)) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unitIndex = 0

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex += 1
  }

  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unitIndex]}`
}
